import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick, sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import { format, parseISO } from 'date-fns';
import { dropTask, restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { eq, gt } from 'ember-truth-helpers';
import AddModal from 'garaje/components/flows/sections/required-documents/add-modal';
import UiCard from 'garaje/components/ui/card';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiWell from 'garaje/components/ui/well';
import UiWellItem from 'garaje/components/ui/well/item';
import type { ReuploadPolicy } from 'garaje/models/custom-user-document-template';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { cached, localCopy } from 'tracked-toolbox';

function policyPillLabel(doc: RequiredDocumentSignature): string {
  switch (doc.reuploadPolicy) {
    case 'always':
      return 'Every Visit';
    case 'never':
      return 'No Re-upload Required';
    case 'after_days':
      return `Every ${doc.reuploadDays ?? '365'} Days`;
    case 'on_expiration':
      return 'On Document Expiration';
    case 'on_date_yearly':
      return doc.reuploadDate ? `Yearly on ${format(parseISO(doc.reuploadDate), 'MMM d')}` : 'Yearly on Date';
    default:
      return '';
  }
}

export interface RequiredDocumentSignature {
  id: string;
  title: string;
  subtitle?: string;
  policyName?: string;
  reuploadPolicy?: ReuploadPolicy;
  reuploadDays?: string;
  reuploadDate?: string; // ISO date string
  position: number;
  isSaving?: boolean;
  configurationId?: string;
}

interface FlowsSectionsRequiredDocumentsListComponentSignature {
  Args: {
    documents: RequiredDocumentSignature[];
    documentUploadPageId?: string;
    isDisabled?: boolean;
    isEnabled?: boolean;
    onDeleteDocument?: (document: RequiredDocumentSignature) => void;
    onDocumentsChanged?: (change?: {
      id: string;
      action: 'added' | 'removed';
      configuration?: UserDocumentTemplateConfigurationModel;
    }) => void;
    onEditDocument?: (document: RequiredDocumentSignature) => void;
    onReorderDocuments?: (documents: RequiredDocumentSignature[]) => Promise<void>;
    onToggleEnabled?: (value: boolean) => void;
  };
}

export default class FlowsSectionsRequiredDocumentsListComponent extends Component<FlowsSectionsRequiredDocumentsListComponentSignature> {
  @service declare store: Store;
  @service declare flashMessages: FlashMessagesService;

  @localCopy('args.isEnabled', false) declare isEnabled: boolean;
  @tracked showingAddModal = false;
  @tracked editingDocument: RequiredDocumentSignature | undefined = undefined;
  @tracked _documentToDelete: RequiredDocumentSignature | null = null;
  @tracked _newDocuments: RequiredDocumentSignature[] = [];

  get isShowingDeleteConfirmation(): boolean {
    return !!this._documentToDelete;
  }

  @cached
  get allDocuments(): RequiredDocumentSignature[] {
    // _newDocuments takes precedence: dedup any doc already reflected in args.documents
    // (the store reload in add-modal makes args.documents the source of truth after save)
    const newDocIds = new Set(this._newDocuments.map((d) => d.id));
    const fromArgs = this.args.documents.filter((d) => !newDocIds.has(d.id));
    return [...fromArgs, ...this._newDocuments];
  }

  get existingDocumentIds(): string[] {
    return this.allDocuments.map((d) => d.id);
  }

  updateDocumentPositions = restartableTask(async (documents: RequiredDocumentSignature[]) => {
    if (this.args.onReorderDocuments) {
      await this.args.onReorderDocuments(documents);
    } else {
      this._newDocuments = documents.map((doc, index) => ({ ...doc, position: index + 1 }));
    }
  });

  toggleEnabled = (value: boolean): void => {
    this.isEnabled = value;
    this.args.onToggleEnabled?.(value);
  };

  openAddModal = (): void => {
    this.showingAddModal = true;
  };

  closeAddModal = (): void => {
    this.showingAddModal = false;
    this.editingDocument = undefined;
  };

  handleDocumentSaved = (document: RequiredDocumentSignature): void => {
    if (this.editingDocument) {
      // If the template was swapped during edit (different ID), remove the old one
      const swapped = this.editingDocument.id !== document.id;
      if (swapped) {
        this._newDocuments = this._newDocuments.filter((d) => d.id !== this.editingDocument!.id);
        this.args.onDocumentsChanged?.({ id: this.editingDocument.id, action: 'removed' });
      }

      const existsInNew = this._newDocuments.some((d) => d.id === document.id);
      if (existsInNew) {
        this._newDocuments = this._newDocuments.map((d) =>
          d.id === document.id ? { ...document, position: d.position } : d,
        );
      } else {
        // Document is new or came from args.documents — add to local state
        const existing = this.editingDocument;
        this._newDocuments = [
          ...this._newDocuments,
          { ...document, position: existing?.position ?? this.allDocuments.length + 1 },
        ];
      }
    } else {
      this._newDocuments = [...this._newDocuments, { ...document, position: this.allDocuments.length + 1 }];
    }
    // Notify the parent controller with the template ID and configuration so it can track added
    // documents. This ensures documents and their configurations survive component destruction.
    const configuration = document.configurationId
      ? this.store.peekRecord('user-document-template-configuration', document.configurationId) ?? undefined
      : undefined;
    this.args.onDocumentsChanged?.({ id: document.id, action: 'added', configuration });
  };

  editDocument = (document: RequiredDocumentSignature): void => {
    this.editingDocument = document;
    this.showingAddModal = true;
  };

  confirmDeleteDocument = (document: RequiredDocumentSignature): void => {
    this._documentToDelete = document;
  };

  cancelDelete = (): void => {
    this._documentToDelete = null;
  };

  removeDocument = dropTask(async () => {
    const document = this._documentToDelete;
    if (!document) return;

    try {
      if (this.args.documentUploadPageId) {
        await this.store
          .adapterFor('document-upload-page')
          .removeFromRelationship(
            'document-upload-page',
            this.args.documentUploadPageId,
            'custom-user-document-templates',
            [{ type: 'custom-user-document-templates', id: document.id }],
          );
        // Reload so the Ember Data store reflects the removed relationship
        await this.store.findRecord('document-upload-page', this.args.documentUploadPageId, {
          reload: true,
          include: 'custom-user-document-templates',
        });
      }

      if (this.args.onDeleteDocument) {
        this.args.onDeleteDocument(document);
      } else {
        this._newDocuments = this._newDocuments.filter((d) => d.id !== document.id);
      }

      this.args.onDocumentsChanged?.({ id: document.id, action: 'removed' });
      this.flashMessages.showAndHideFlash('success', 'Document removed.');
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    } finally {
      this._documentToDelete = null;
    }
  });

  <template>
    <SettingsPanel class="p-0 bg-carbon-5" data-test-required-documents-section as |panel|>
      <panel.header
        @iconUri="/assets/images/features/signature.svg"
        @title="Required document uploads"
        @description="Require visitors to upload specific documents before they can check in."
        class="p-4"
        as |header|
      >
        <header.controls>
          <div class="bg-white">
            <label class="toggle">
              <input
                type="checkbox"
                checked={{this.isEnabled}}
                {{on "change" (pick "target.checked" this.toggleEnabled)}}
                disabled={{@isDisabled}}
                data-test-feature-toggle
                aria-label="Enable Feature"
              />
              <span></span>
            </label>
          </div>
        </header.controls>
      </panel.header>

      <panel.body class="bg-carbon-5" @isOpen={{this.isEnabled}}>
        <div class="relative z-10 -mx-px py-3 shadow-md shadow-gray-50">
          <div class="px-4 flex items-center justify-between">
            <span class="text-sm font-normal leading-[21px] text-carbon-60" data-test-document-count>
              {{this.allDocuments.length}}
              {{if (eq this.allDocuments.length 1) "document" "documents"}}
              required
            </span>
            {{#unless @isDisabled}}
              <button
                type="button"
                class="btn btn--small btn--brandOutline self-start"
                data-test-add-new-document-button="true"
                {{on "click" this.openAddModal}}
              >
                + Add document
              </button>
            {{/unless}}
          </div>
        </div>

        <UiWell
          class="px-12 mt-4"
          data-test-required-documents-list
          {{sortableGroup onChange=(perform this.updateDocumentPositions)}}
        >
          <div class="-mb-4">
            {{#each (sortBy "position" this.allDocuments) as |document|}}
              <UiWellItem
                data-test-sortable-required-document
                data-test-id={{document.id}}
                class="mb-4 trans-all"
                {{sortableItem model=document}}
              >
                <UiCard class="is-dragging:shadow-lg-tall trans-box-shadow w-full">
                  <div class="flex space-x-4 items-center justify-between">
                    <div class="flex-auto flex flex-col space-y-1 min-w-0">
                      <div class="flex items-center space-x-2">
                        <h4 class="mb-0 truncate">{{document.title}}</h4>
                        {{#if document.reuploadPolicy}}
                          <span
                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gem-10 text-gem-60"
                          >
                            {{policyPillLabel document}}
                          </span>
                        {{/if}}
                      </div>
                      {{#if document.subtitle}}
                        <p class="text-sm text-carbon-50 mb-0 truncate">{{document.subtitle}}</p>
                      {{/if}}
                    </div>

                    <div class="flex items-center space-x-2">
                      <button
                        type="button"
                        class="btn btn--small btn--brand"
                        {{on "click" (fn this.editDocument document)}}
                        data-test-edit-document
                      >
                        {{if @isDisabled "View" "Edit"}}
                      </button>
                    </div>
                  </div>
                </UiCard>
                {{#unless @isDisabled}}
                  <div class="flex items-center">
                    <div class="flex-1">
                      {{#if (gt this.allDocuments.length 1)}}
                        <div
                          class="icon-reSort
                            {{if
                              document.isSaving
                              'icon-reSort-disabled'
                              'handle cursor-row-resize hover:cursor-row-resize'
                            }}"
                          {{sortableHandle}}
                        />
                      {{/if}}
                    </div>
                    <button
                      type="button"
                      class="btn btn--small btn--square btn--hoverFill"
                      {{on "click" (fn this.confirmDeleteDocument document)}}
                      aria-label="Delete document"
                      data-test-delete-document
                    >
                      <span class="icon-trash"></span>
                    </button>
                  </div>
                {{/unless}}
              </UiWellItem>
            {{/each}}
          </div>
        </UiWell>

        <p class="-mx-px p-4 pt-0 text-sm mb-0">
          Learn more about adding and configuring
          <a href="https://envoy.help/en/articles/document-uploads" target="_blank" rel="noopener">document uploads</a>.
        </p>

      </panel.body>
    </SettingsPanel>

    {{#if this.showingAddModal}}
      <AddModal
        @documentUploadPageId={{@documentUploadPageId}}
        @existingDocument={{this.editingDocument}}
        @existingDocumentIds={{this.existingDocumentIds}}
        @onClose={{this.closeAddModal}}
        @onSaved={{this.handleDocumentSaved}}
      />
    {{/if}}

    {{#if this.isShowingDeleteConfirmation}}
      <ConfirmationModal
        @title="Remove document?"
        @ctaText="Remove document"
        @handleTaskAction={{this.removeDocument}}
        @onClose={{this.cancelDelete}}
        @closeButtonClass="btn--outlineFill"
      >
        <p>
          Visitors will no longer be required to upload
          <strong>{{this._documentToDelete.title}}</strong>
          when signing in. You can add it back later.
        </p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
